import React, { useState, useCallback } from 'react';
import { Radio, Space, Input, RadioChangeEvent, notification } from 'antd';
import { send2FACode, verifyCode } from 'services/api';
import Button from 'components/Button';
import { Customer } from './types';

interface VerificationProps {
  onComplete: () => void;
  customer: Customer;
}

const Verification = ({ onComplete, customer }: VerificationProps) => {
  const [isCodeSent, setCodeSent] = useState(false);
  const [showOptions, setShowOptions] = useState(true);
  const [showResendLink, setShowResendLink] = useState(true);
  const [showResendConfirmMsg, setShowResendConfirmMsg] = useState(false);
  const [isResendProcess, setIsResendProcess] = useState(false);
  const [isEmailSelected, setEmailSelected] = useState(true);
  const [code, setCode] = useState('');

  const onChange = (e: RadioChangeEvent) => {
    if (showResendConfirmMsg) {
      setShowResendConfirmMsg(false);
    }
    setEmailSelected(e.target.value);
  };

  const handleSend2FACode = useCallback(async () => {
    try {
      if (!isCodeSent) {
        setShowOptions(false);
      }
      setCodeSent(true);
      if (isResendProcess) {
        setShowResendConfirmMsg(true);
      }
      setCode('');
      await send2FACode(customer?.GUID || '', isEmailSelected);
    } catch (error) {
      notification.error({
        message: 'Failed',
        description: 'Send 2FA code failed',
      });
    }
  }, [customer?.GUID, isCodeSent, isEmailSelected, isResendProcess]);

  const handleVerify2FACode = async () => {
    try {
      await verifyCode(customer?.GUID || '', code);
      onComplete();
    } catch (error) {
      notification.error({
        message: 'Failed',
        description: 'Invalid code!',
      });
    }
  };

  return (
    <div className="verification-section">
      <p>{customer.Page_Content}</p>
      {isCodeSent && (
        <>
          <p>
            A security code was sent. Please enter the code in the box below.
          </p>
          <div className="security-code-wrapper">
            <label>Security Code:</label>
            <div>
              <Space>
                <Input value={code} onChange={(e) => setCode(e.target.value)} />
                <Button disabled={!code} onClick={handleVerify2FACode}>
                  Submit
                </Button>
              </Space>
              {showResendLink && (
                <div>
                  <button
                    className="resend-button"
                    onClick={() => {
                      setShowOptions(true);
                      setIsResendProcess(true);
                      setShowResendLink(false);
                      setCode('');
                    }}
                  >
                    Resend code
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      {showOptions && (
        <>
          <Radio.Group value={isEmailSelected} onChange={onChange}>
            <Space direction="vertical">
              <Radio value={true}>Email: {customer?.Customer_Email}</Radio>
              <Radio value={false}>Text: {customer?.Customer_Phone}</Radio>
            </Space>
          </Radio.Group>
          <div className="button-wrapper">
            <Button onClick={handleSend2FACode}>
              {isCodeSent ? 'Resend' : 'Submit'}
            </Button>
          </div>
          {showResendConfirmMsg && (
            <p className="resend-confirm">
              A security code was sent to {isEmailSelected ? 'Email' : 'Phone'}:{' '}
              {isEmailSelected
                ? customer?.Customer_Email
                : customer?.Customer_Phone}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default Verification;
